// The planning board (M7, WP7.2; plan §5.6): the next 14 days at a branch, day by day — the
// batches planned (product, amount, recipe version, status) with the feasibility check's
// verdict and its flags in words, and the order lines handed over each day with their
// coverage. A banner says what the run checked and what it couldn't (lesson 12: never "all
// clear" when incomplete). From here: plan, move, resize or cancel a batch, allocate its
// output to an order line or give it back, and check again. Flags recommend; nothing here
// stops the kitchen.

import type { FeasibilityBoard, FeasibilityLineWire } from '@enp/domain/api';
import { canOpenArea } from '@enp/domain/permissions';
import { addDays, formatPlainDate } from '@enp/domain/time';
import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  formatTime,
  IconButton,
  LoadingSkeleton,
  PageHeader,
  Skeleton,
  StatusBadge,
  toast,
} from '@enp/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  Ban,
  CalendarClock,
  CalendarRange,
  CircleCheck,
  ClipboardCheck,
  ExternalLink,
  MoreHorizontal,
  Plus,
  RefreshCw,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { meQueryOptions } from '../../auth/session.js';
import {
  boardDays,
  boardBatch,
  lineChoices,
  lineNextSteps,
  undatedLines,
  openOnLine,
  plannedFor,
  productOfBatch,
  type BoardBatch,
  type BoardDay,
  planDayForLine,
} from '../../planning/board.js';
import { AllocateDialog, MoveBatchDialog, type AllocatingLine } from '../../planning/dialogs.js';
import {
  BoardLink,
  CoverageBanner,
  FlagExplained,
  LineCoverage,
  RunFreshness,
  VerdictBadge,
} from '../../planning/parts.js';
import {
  boardQuery,
  boardSearchSchema,
  planningKeys,
  recompute,
  type BoardSearch,
} from '../../planning/queries.js';
import { dayHeading, dayText, qtyText, sourcesText, verdictCounts } from '../../planning/words.js';
import { CancelBatchDialog } from '../../production/cancel-dialog.js';
import { BatchStatusBadge, cell, numCell, PlainTable } from '../../production/parts.js';
import { PlanBatchDialog, type PlanForLine } from '../../production/plan-dialog.js';
import { KeyHints, usePageKeys } from '../../purchasing/parts.js';
import { useDocumentTitle } from '../../shell/page.js';
import { ErrorPanel } from '../../shell/route-states.js';
import { FilterSelect } from '../../stock/components.js';
import { accountToday } from '../../stock/labels.js';
import { clean, locationsQuery } from '../../stock/queries.js';

export const Route = createFileRoute('/_app/planning')({
  validateSearch: boardSearchSchema,
  component: PlanningPage,
});

type Dialogs =
  | {
      kind: 'plan';
      day: string;
      search?: string | undefined;
      /** The product the search means (a flag names it): picked at once (UI7A-204). */
      product?: string | undefined;
      amount?: string | undefined;
      forLine?: PlanForLine | undefined;
    }
  | { kind: 'move'; item: BoardBatch }
  | { kind: 'cancel'; item: BoardBatch }
  | { kind: 'allocate-batch'; item: BoardBatch }
  | { kind: 'allocate-line'; line: AllocatingLine }
  | null;

function PlanningPage() {
  useDocumentTitle('Planning');
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const { data: me } = useSuspenseQuery(meQueryOptions());
  const timeZone = me.account.timezone;
  const today = accountToday(me.account);
  const tomorrow = addDays(today, 1);
  const canPlan = me.permissions.includes('production.plan');
  const canOrder = me.permissions.includes('orders.edit');
  const seesOrders = canOpenArea(me.permissions, 'orders');
  const [dialog, setDialog] = useState<Dialogs>(null);
  const setSearch = (patch: Partial<BoardSearch>) =>
    void navigate({ search: (prev) => clean({ ...prev, ...patch }), replace: true });

  const locations = useQuery({
    ...locationsQuery(),
    enabled: canOpenArea(me.permissions, 'stock'),
  });
  const branches = useMemo(() => {
    const seen = new Map<string, string>();
    for (const l of locations.data?.items ?? []) seen.set(l.branch.id, l.branch.name);
    return [...seen].map(([value, label]) => ({ value, label }));
  }, [locations.data]);

  const board = useQuery(boardQuery(search.branch));
  const recheck = useMutation({
    mutationFn: () => recompute(search.branch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: planningKeys.board(search.branch) }),
    onError: () =>
      toast.error('It wasn’t checked again', { description: 'Try again in a moment.' }),
  });
  // Something changed after the run: the server is recomputing; ask again shortly.
  const stale = board.data?.run.stale ?? false;
  useEffect(() => {
    if (!stale) return;
    const t = setTimeout(() => void board.refetch(), 1500);
    return () => clearTimeout(t);
  }, [stale, board]);

  // A link from an order's coverage: bring the batch or line into view, marked.
  const marked = search.focus;
  useEffect(() => {
    if (!search.focus || !board.data) return;
    const el = document.getElementById(`plan-${search.focus}`);
    if (!el) return;
    el.scrollIntoView({ block: 'center' });
    el.focus({ preventScroll: true });
  }, [search.focus, board.data]);
  const focusOn = (id: string) => setSearch({ focus: id, show: undefined });

  const issuesOnly = search.show === 'issues';
  usePageKeys({
    n: canPlan ? () => setDialog({ kind: 'plan', day: today }) : undefined,
    r: () => recheck.mutate(),
    f: () => setSearch({ show: issuesOnly ? undefined : 'issues' }),
  });

  const data = board.data;
  const days = useMemo(() => (data ? boardDays(data, { issuesOnly }) : []), [data, issuesOnly]);
  const undated = data ? undatedLines(data) : [];
  const branchName = data?.run.branch.name;

  const header = (
    <PageHeader
      title="Planning"
      description={`The next ${data?.run.horizon_days ?? 14} days${branchName ? ` at ${branchName}` : ''}: what’s planned, what’s promised, and whether it can be made. Flags are advice: nothing here stops the kitchen.`}
      actions={
        <>
          {canOrder && (
            <Button variant="secondary" asChild>
              <Link to="/orders/new">
                <Plus aria-hidden />
                New order
              </Link>
            </Button>
          )}
          {canPlan && (
            <Button
              icon={<Plus aria-hidden />}
              aria-keyshortcuts="n"
              onClick={() => setDialog({ kind: 'plan', day: today })}
            >
              Plan a batch
            </Button>
          )}
        </>
      }
    />
  );

  const hints: [string, string][] = [
    ...(canPlan ? ([['n', 'plan a batch']] as [string, string][]) : []),
    ['r', 'check again'],
    ['f', issuesOnly ? 'show every day' : 'only what needs a look'],
  ];

  return (
    <>
      {header}
      <div
        className="mb-4 flex flex-wrap items-center justify-between gap-3"
        role="search"
        aria-label="Board filters"
      >
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          {branches.length > 1 && (
            <FilterSelect
              label="Branch"
              value={search.branch}
              allLabel={branchName ? `${branchName} (yours)` : 'Your branch'}
              options={branches}
              onChange={(v) => setSearch({ branch: v, focus: undefined })}
            />
          )}
          <FilterSelect
            label="Show"
            value={search.show}
            allLabel="Every day"
            options={[{ value: 'issues', label: 'Only what needs a look' }]}
            onChange={(v) => setSearch({ show: v as BoardSearch['show'] })}
          />
          <KeyHints keys={hints} />
        </div>
        {data && (
          <RunFreshness
            run={data.run}
            recomputing={recheck.isPending}
            refreshing={board.isFetching && !board.isPending}
            onRecheck={() => recheck.mutate()}
            canRecheck
          />
        )}
      </div>

      {board.isError && !data ? (
        <ErrorPanel
          error={board.error}
          titleAs="h2"
          what="The planning board"
          onRetry={() => void board.refetch()}
        />
      ) : !data ? (
        <BoardSkeleton />
      ) : (
        <div
          className="flex flex-col gap-6"
          aria-busy={recheck.isPending || data.run.stale || undefined}
        >
          <div className="flex flex-col gap-2">
            {/* Results about to change say so (UI7A-11), without fading them below contrast. */}
            {(recheck.isPending || data.run.stale) && (
              <p
                className="flex items-center gap-1.5 rounded-md border border-dashed border-border-strong px-3 py-1.5 text-sm text-fg"
                data-testid="updating"
              >
                <RefreshCw aria-hidden className="size-4 shrink-0 animate-spin-slow" />
                Updating: what’s below is the last check and may change in a moment.
              </p>
            )}
            <CoverageBanner coverage={data.coverage} onFocus={focusOn} />
            {data.coverage.batches.total > 0 && (
              <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted">
                <span>Batches:</span>
                {verdictCounts(data.coverage)
                  .filter((c) => c.n > 0)
                  .map((c) => (
                    <span key={c.label} className="tabular-nums text-fg">
                      {c.n} {c.label.toLowerCase()}
                    </span>
                  ))}
              </p>
            )}
          </div>

          {data.coverage.status === 'nothing_planned' ? (
            <EmptyState
              icon={CalendarRange}
              title="Nothing to check yet"
              description="Plan a batch, or take a customer order with a promised time: the board checks every day of the next two weeks against your stock, deliveries and recipes."
              action={
                <>
                  {canPlan && (
                    <Button
                      icon={<Plus aria-hidden />}
                      onClick={() => setDialog({ kind: 'plan', day: today })}
                    >
                      Plan a batch
                    </Button>
                  )}
                  {canOrder && (
                    <Button variant="secondary" asChild>
                      <Link to="/orders/new">Take an order</Link>
                    </Button>
                  )}
                </>
              }
            />
          ) : issuesOnly && days.length === 0 && undated.length === 0 ? (
            <EmptyState
              compact
              tone="success"
              icon={CircleCheck}
              title="Nothing needs a look"
              description={`Every batch checked in the next ${data.coverage.horizon.days} days can be made and every order line checked is covered.`}
              action={
                <Button
                  variant="secondary"
                  icon={<X aria-hidden />}
                  onClick={() => setSearch({ show: undefined })}
                >
                  Show every day
                </Button>
              }
            />
          ) : (
            <>
              {days.map((d) =>
                d.kind === 'empty' ? (
                  <p
                    key={d.from}
                    className="rounded-md border border-dashed border-border px-4 py-3 text-sm text-muted"
                  >
                    {dayText(d.from)} – {dayText(d.to)}: nothing planned or promised.
                  </p>
                ) : (
                  <DaySection
                    key={d.day}
                    d={d}
                    board={data}
                    today={today}
                    tomorrow={tomorrow}
                    timeZone={timeZone}
                    canPlan={canPlan}
                    seesOrders={seesOrders}
                    marked={marked}
                    branch={search.branch}
                    open={setDialog}
                    focusOn={focusOn}
                  />
                ),
              )}
              {undated.length > 0 && !issuesOnly && (
                <section aria-labelledby="undated-title" className="flex flex-col gap-3">
                  <h2 id="undated-title" className="font-display text-lg font-semibold text-fg">
                    Orders without a promised time
                  </h2>
                  <LinesTable
                    label="Orders without a promised time"
                    lines={undated}
                    board={data}
                    timeZone={timeZone}
                    today={today}
                    canPlan={canPlan}
                    seesOrders={seesOrders}
                    marked={marked}
                    branch={search.branch}
                    open={setDialog}
                    focusOn={focusOn}
                  />
                </section>
              )}
            </>
          )}
        </div>
      )}

      {dialog?.kind === 'plan' && (
        <PlanBatchDialog
          day={dialog.day}
          today={today}
          timeZone={timeZone}
          branchId={search.branch ?? data?.run.branch.id}
          search={dialog.search}
          product={dialog.product}
          amount={dialog.amount}
          forLine={dialog.forLine}
          onClose={() => setDialog(null)}
          onPlanned={(b) => {
            setDialog(null);
            void queryClient.invalidateQueries({ queryKey: planningKeys.all });
            toast.success(`Batch ${b.number} planned`, {
              description: `${b.product.name}, ${dayText(b.planned_day)} at ${formatTime(b.planned_start, { timeZone })}. The board checks it now.`,
              action: { label: 'Show it', onClick: () => focusOn(b.id) },
            });
          }}
        />
      )}
      {dialog?.kind === 'move' && data && (
        <MoveBatchDialog
          item={dialog.item}
          board={data}
          today={today}
          timeZone={timeZone}
          onClose={() => setDialog(null)}
          onCancelBatch={() => setDialog({ kind: 'cancel', item: dialog.item })}
          onPlanAnother={() =>
            setDialog({
              kind: 'plan',
              day: dialog.item.batch.day,
              search: dialog.item.product?.name,
            })
          }
        />
      )}
      {dialog?.kind === 'cancel' && (
        <CancelBatchDialog
          batch={{
            id: dialog.item.batch.batch_id,
            number: dialog.item.batch.number,
            status: dialog.item.batch.status,
            product: { name: dialog.item.product?.name ?? 'This batch' },
          }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'allocate-batch' && data && (
        <AllocateDialog
          from="batch"
          item={boardBatch(data, dialog.item.batch)}
          board={data}
          timeZone={timeZone}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'allocate-line' && data && (
        <AllocateDialog
          from="line"
          line={dialog.line}
          board={data}
          timeZone={timeZone}
          onClose={() => setDialog(null)}
          onPlanForLine={(l) => setDialog(planForLine(l, today, timeZone, today))}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------- a day

function DaySection({
  d,
  board,
  today,
  tomorrow,
  timeZone,
  canPlan,
  seesOrders,
  marked,
  branch,
  open,
  focusOn,
}: {
  d: BoardDay;
  board: FeasibilityBoard;
  today: string;
  tomorrow: string;
  timeZone: string;
  canPlan: boolean;
  seesOrders: boolean;
  marked: string | undefined;
  branch: string | undefined;
  open: (d: Dialogs) => void;
  focusOn: (id: string) => void;
}) {
  const title = dayHeading(d.day, today, tomorrow);
  const id = `day-${d.day}`;
  const summary = [
    d.batches.length > 0 && `${d.batches.length} ${d.batches.length === 1 ? 'batch' : 'batches'}`,
    d.lines.length > 0 && `${d.lines.length} order ${d.lines.length === 1 ? 'line' : 'lines'}`,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <section aria-labelledby={id} className="flex flex-col gap-3" data-day={d.day}>
      {/* The day stays in view while its rows scroll under the top bar (UI7A-8). */}
      <div className="sticky top-14 z-10 -mx-2 flex flex-wrap items-center justify-between gap-2 border-b border-border bg-surface px-2 py-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h2 id={id} className="font-display text-lg font-semibold text-fg">
            {title}
          </h2>
          <span className="text-sm text-muted">{summary || 'Nothing planned or promised'}</span>
          {d.issues > 0 && (
            <StatusBadge status="warning">
              {d.issues} {d.issues === 1 ? 'needs' : 'need'} a look
            </StatusBadge>
          )}
          {d.unchecked > 0 && <StatusBadge status="neutral">{d.unchecked} not checked</StatusBadge>}
          {d.issues + d.unchecked === 0 && summary && (
            <StatusBadge status="success">All checked</StatusBadge>
          )}
        </div>
        {canPlan && (
          <Button
            variant="ghost"
            size="sm"
            icon={<Plus aria-hidden />}
            onClick={() => open({ kind: 'plan', day: d.day })}
          >
            Plan a batch on {formatPlainDate(d.day)}
          </Button>
        )}
      </div>

      {d.batches.length > 0 && (
        <PlainTable
          label={`Batches on ${dayText(d.day)}`}
          head={[
            'Starts',
            'Product',
            'Amount',
            'Check',
            <span key="a" className="sr-only">
              Actions
            </span>,
          ]}
          align={['left', 'left', 'right', 'left', 'right']}
          columnClass={GRID}
          tableClass={GRID_TABLE}
          className="rounded-lg border border-border bg-surface-raised"
        >
          {d.batches.map((b) => (
            <BatchRow
              key={b.batch.batch_id}
              item={b}
              board={board}
              timeZone={timeZone}
              canPlan={canPlan}
              seesOrders={seesOrders}
              marked={marked === b.batch.batch_id}
              branch={branch}
              open={open}
            />
          ))}
        </PlainTable>
      )}

      {d.lines.length > 0 && (
        <LinesTable
          label={`Orders due on ${dayText(d.day)}`}
          lines={d.lines}
          board={board}
          timeZone={timeZone}
          today={today}
          canPlan={canPlan}
          seesOrders={seesOrders}
          marked={marked}
          branch={branch}
          open={open}
          focusOn={focusOn}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------- a batch

function BatchRow({
  item,
  board,
  timeZone,
  canPlan,
  seesOrders,
  marked,
  branch,
  open,
}: {
  item: BoardBatch;
  board: FeasibilityBoard;
  timeZone: string;
  canPlan: boolean;
  seesOrders: boolean;
  marked: boolean;
  branch: string | undefined;
  open: (d: Dialogs) => void;
}) {
  const b = item.batch;
  const unit = item.product?.base_uom ?? 'ea';
  const name = item.product?.name ?? 'Unknown product';
  const short = Number(b.expected_yield) < Number(b.planned_qty) && b.evaluated;
  const orders = item.allocations.filter((a) => a.order);
  const forLine = item.detail?.for_order_line ?? null;
  // Allocating makes sense only where an order line can take it (D-074, UI7A-9).
  // ...and only where one can still take it (D-074): never a dialog with nothing to choose.
  const canAllocate = lineChoices(board, item).some((c) => !c.blocked);
  return (
    <tr
      id={`plan-${b.batch_id}`}
      tabIndex={-1}
      data-batch={b.number}
      data-marked={marked || undefined}
      className={cn(
        'scroll-mt-32 outline-none',
        marked && 'bg-primary-subtle ring-2 ring-inset ring-primary',
      )}
    >
      <td className={cell}>
        <span className="flex flex-col items-start gap-1 leading-tight">
          <time dateTime={b.planned_start} className="tabular-nums text-fg">
            {formatTime(b.planned_start, { timeZone })}
          </time>
          <span className="whitespace-nowrap font-mono text-xs text-muted">{b.number}</span>
          <BatchStatusBadge status={b.status} />
        </span>
      </td>
      <td className={cell}>
        <span className="flex flex-col gap-0.5 leading-tight">
          <Link
            to="/production/$batchId"
            params={{ batchId: b.batch_id }}
            className="font-medium text-fg hover:underline"
          >
            {name}
          </Link>
          <span className="text-xs text-muted">
            <span className="whitespace-nowrap">
              {item.detail
                ? `Recipe version ${item.detail.recipe_version.version_no}`
                : 'Recipe pinned'}
            </span>
            {item.detail?.station && (
              <>
                {' · '}
                <span className="whitespace-nowrap">at station {item.detail.station.name}</span>
              </>
            )}
          </span>
          {forLine && (
            <span className="text-xs font-medium text-fg" data-testid="planned-for">
              Planned for{' '}
              <span className="whitespace-nowrap font-mono">
                {forLine.order_number} line {forLine.position}
              </span>
            </span>
          )}
          {orders.map((a) => (
            <span key={a.id} className="whitespace-nowrap text-xs text-fg">
              For{' '}
              {seesOrders && a.order ? (
                <Link
                  to="/orders/$orderId"
                  params={{ orderId: a.order.id }}
                  className="font-mono text-primary-text underline underline-offset-2"
                >
                  {a.order.number}
                </Link>
              ) : (
                <span className="font-mono">{a.order?.number}</span>
              )}{' '}
              line {a.order?.position}: <span className="tabular-nums">{qtyText(a.qty, unit)}</span>
            </span>
          ))}
          {item.detail?.note && (
            <span className="text-xs italic text-muted">“{item.detail.note}”</span>
          )}
        </span>
      </td>
      <td className={numCell}>
        <span className="flex flex-col items-end gap-0.5 leading-tight">
          <span className="font-medium text-fg">{qtyText(b.planned_qty, unit)}</span>
          {short && (
            <span className="whitespace-normal text-xs font-medium text-state-danger">
              expected <span className="whitespace-nowrap">{qtyText(b.expected_yield, unit)}</span>
            </span>
          )}
          {Number(item.forOrders) > 0 && (
            <span className="text-xs text-muted">{qtyText(item.forOrders, unit)} for orders</span>
          )}
        </span>
      </td>
      <td className={cell}>
        <div className="flex max-w-[65ch] flex-col items-start gap-2">
          <VerdictBadge verdict={b.verdict} />
          {b.flags.length > 0 && (
            <ul aria-label={`Flags on ${b.number}`} className="flex flex-col gap-2">
              {b.flags.map((f, i) => (
                <FlagExplained
                  key={`${f.code}-${i}`}
                  flag={f}
                  products={board.products}
                  allocations={board.allocations}
                  action={<FlagAction flag={f} day={b.day} canPlan={canPlan} open={open} />}
                />
              ))}
            </ul>
          )}
          {!b.evaluated && b.flags.length === 0 && b.not_evaluated_reasons.length > 0 && (
            <p className="text-sm text-fg">{b.not_evaluated_reasons.join(' ')}</p>
          )}
          {b.causes.length > 0 && (
            <ul aria-label={`What ${b.number} depends on`} className="flex flex-col gap-1">
              {b.causes.map((c, i) => (
                <li key={i} className="text-sm text-fg" data-testid="upstream-cause">
                  <span className="font-semibold">Because of an earlier batch: </span>
                  {c.message}{' '}
                  {c.batch_id && (
                    <span className="text-muted">
                      What to do: fix{' '}
                      <BoardLink focus={c.batch_id} branch={branch} className="font-mono">
                        {c.number}
                      </BoardLink>{' '}
                      first.
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </td>
      <td className={cn(cell, 'text-right')}>
        {canPlan ? (
          // Not modal: the page keeps its heading and landmarks while the menu is open (UI7A-12).
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <IconButton
                label={`Actions for ${b.number}`}
                variant="ghost"
                icon={<MoreHorizontal aria-hidden />}
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-60"
              // Inside the main landmark: nothing of the page sits outside one (UI7A-12).
              container={document.getElementById('main-content')}
            >
              <DropdownMenuLabel>{b.number}</DropdownMenuLabel>
              {canAllocate && (
                <DropdownMenuItem onSelect={() => open({ kind: 'allocate-batch', item })}>
                  <ClipboardCheck aria-hidden />
                  Allocate to an order…
                </DropdownMenuItem>
              )}
              {b.status === 'planned' && (
                <DropdownMenuItem onSelect={() => open({ kind: 'move', item })}>
                  <CalendarClock aria-hidden />
                  Move or change amount…
                </DropdownMenuItem>
              )}
              <DropdownMenuItem asChild>
                <Link to="/production/$batchId" params={{ batchId: b.batch_id }}>
                  <ExternalLink aria-hidden />
                  Open the batch
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-state-danger"
                onSelect={() => open({ kind: 'cancel', item })}
              >
                <Ban aria-hidden />
                Cancel batch…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button variant="ghost" size="icon" asChild>
            <Link
              to="/production/$batchId"
              params={{ batchId: b.batch_id }}
              aria-label={`Open ${b.number}`}
              title={`Open ${b.number}`}
            >
              <ExternalLink aria-hidden />
            </Link>
          </Button>
        )}
      </td>
    </tr>
  );
}

/** A flag's next step, where the Admin can take it. */
function FlagAction({
  flag,
  day,
  canPlan,
  open,
}: {
  flag: FeasibilityBoard['batches'][number]['flags'][number];
  day: string;
  canPlan: boolean;
  open: (d: Dialogs) => void;
}) {
  if (flag.code === 'intermediate_not_planned' && canPlan) {
    return (
      <Button
        variant="link"
        className="self-start text-sm"
        onClick={() =>
          open({
            kind: 'plan',
            day,
            search: flag.data.component.name,
            product: flag.data.component.id,
            amount: flag.data.missing,
          })
        }
      >
        Plan a batch of {flag.data.component.name}…
      </Button>
    );
  }
  if (flag.code === 'missing_data') {
    const d = flag.data;
    if (
      d.reason === 'no_recipe' ||
      d.reason === 'recipe_loop' ||
      d.reason === 'unknown_component' ||
      d.reason === 'made_to_order_without_recipe'
    ) {
      return (
        <Link to="/recipes" className="self-start text-sm font-medium text-primary-text underline">
          Open Recipes
        </Link>
      );
    }
    if (d.reason === 'missing_conversion' || d.reason === 'no_shelf_life') {
      return (
        <Link
          to="/catalogue/$productId"
          params={{ productId: d.product.id }}
          className="self-start text-sm font-medium text-primary-text underline"
        >
          Open {d.product.name ?? 'the product'} in the catalogue
        </Link>
      );
    }
  }
  return null;
}

// ---------------------------------------------------------------- order lines

function LinesTable({
  label,
  lines,
  board,
  timeZone,
  today,
  canPlan,
  seesOrders,
  marked,
  branch,
  open,
  focusOn,
}: {
  label: string;
  lines: FeasibilityLineWire[];
  board: FeasibilityBoard;
  timeZone: string;
  /** The business's trading day (accountToday, D-062). */
  today: string;
  canPlan: boolean;
  seesOrders: boolean;
  marked: string | undefined;
  branch: string | undefined;
  open: (d: Dialogs) => void;
  focusOn: (id: string) => void;
}) {
  return (
    <PlainTable
      label={label}
      head={[
        'Promised',
        'Order',
        'Amount',
        'Coverage',
        <span key="a" className="sr-only">
          Actions
        </span>,
      ]}
      align={['left', 'left', 'right', 'left', 'right']}
      columnClass={GRID}
      tableClass={GRID_TABLE}
      className="rounded-lg border border-border bg-surface-raised"
    >
      {lines.map((l) => {
        const product = l.product_id ? board.products.find((p) => p.id === l.product_id) : null;
        const open_ = openOnLine(board, l);
        const allocating: AllocatingLine = {
          order_line_id: l.order_line_id,
          order_id: l.order_id,
          order_number: l.order_number,
          position: l.position,
          product_id: l.product_id,
          label: product?.name ?? l.description ?? 'Custom item',
          qty: l.qty,
          promised_at: l.promised_at,
          open: open_,
        };
        const unit = product?.base_uom ?? 'ea';
        const what = product?.name ?? l.description ?? 'Custom item';
        const from = sourcesText(l, unit);
        const covered = l.status === 'covered_by_batch' || l.status === 'covered_by_stock';
        const isMarked = marked === l.order_line_id;
        return (
          <tr
            key={l.order_line_id}
            id={`plan-${l.order_line_id}`}
            tabIndex={-1}
            data-line={`${l.order_number}/${l.position}`}
            data-marked={isMarked || undefined}
            className={cn(
              'scroll-mt-32 outline-none',
              isMarked && 'bg-primary-subtle ring-2 ring-inset ring-primary',
            )}
          >
            <td className={cn(cell, 'whitespace-nowrap tabular-nums')}>
              {l.promised_at ? formatTime(l.promised_at, { timeZone }) : '—'}
            </td>
            <td className={cell}>
              <span className="flex flex-col gap-0.5 leading-tight">
                <span>
                  {seesOrders ? (
                    <Link
                      to="/orders/$orderId"
                      params={{ orderId: l.order_id }}
                      className="font-mono font-medium text-primary-text underline underline-offset-2"
                    >
                      {l.order_number}
                    </Link>
                  ) : (
                    <span className="font-mono font-medium">{l.order_number}</span>
                  )}{' '}
                  <span className="text-muted">line {l.position}</span>
                </span>
                <span className="text-fg">
                  {what}
                  {!l.product_id && <span className="text-muted"> (custom)</span>}
                </span>
              </span>
            </td>
            <td className={numCell}>{qtyText(l.qty, unit)}</td>
            <td className={cell}>
              <LineCoverage
                line={l}
                productOfBatch={productOfBatch(board)}
                linkBatch={(batchId, number) => (
                  <BoardLink focus={batchId} branch={branch} className="font-mono">
                    {number}
                  </BoardLink>
                )}
                from={covered ? from : null}
                actions={
                  <>
                    {canPlan && l.status !== 'not_evaluated' && (
                      <LineNextActions
                        l={l}
                        board={board}
                        allocating={allocating}
                        productName={product?.name}
                        timeZone={timeZone}
                        today={today}
                        open={open}
                        focusOn={focusOn}
                      />
                    )}
                  </>
                }
              />
            </td>
            {/* Actions sit under the words (UI7A-101): this column stays narrow. */}
            <td className={cell} />
          </tr>
        );
      })}
    </PlainTable>
  );
}

/**
 * The next step for a line that isn't covered, or is at risk (UI7A-105): a custom item gets
 * "Plan a batch for this line"; a short line "Plan N more"; the batch to fix first opens on
 * the board; a batch made too late can be moved; allocating stays possible while something
 * is left to allocate. Never an uncovered line without a way forward.
 */
function LineNextActions({
  l,
  board,
  allocating,
  productName,
  timeZone,
  today,
  open,
  focusOn,
}: {
  l: FeasibilityLineWire;
  board: FeasibilityBoard;
  allocating: AllocatingLine;
  productName: string | undefined;
  timeZone: string;
  today: string;
  open: (d: Dialogs) => void;
  focusOn: (id: string) => void;
}) {
  const covered = l.status === 'covered_by_batch' || l.status === 'covered_by_stock';
  if (covered && !l.at_risk) return null;
  const next = lineNextSteps(board, l);
  const planned = plannedFor(board, l.order_line_id);
  // A batch made after the promise, still movable: move it earlier.
  const lateBatch =
    l.reason === 'batch_after_promise'
      ? board.batches.find(
          (b) =>
            b.status === 'planned' &&
            (l.batch_numbers.includes(b.number) ||
              l.ignored_batches.some(
                (x) => x.batch_id === b.batch_id && x.why === 'completes_after_promise',
              )),
        )
      : undefined;
  const canAllocate =
    !covered && (l.product_id !== null || planned !== null) && Number(allocating.open) > 0;
  // Row and flag actions are outlined: the header's "Plan a batch" is the one filled action
  // on the page (UI8C-13).
  const buttons = [
    next.planForLine && (
      <Button
        key="plan-for"
        size="sm"
        variant="secondary"
        icon={<Plus aria-hidden />}
        onClick={() => open(planForLine(allocating, l.day ?? undefined, timeZone, today))}
      >
        Plan a batch for this line…
      </Button>
    ),
    next.planMore !== null && (
      <Button
        key="plan-more"
        size="sm"
        variant="secondary"
        icon={<Plus aria-hidden />}
        onClick={() =>
          open(
            planForLine(
              { ...allocating, open: next.planMore as string },
              l.day ?? undefined,
              timeZone,
              today,
              productName,
            ),
          )
        }
      >
        Plan {next.planMore} more…
      </Button>
    ),
    lateBatch && (
      <Button
        key="move"
        size="sm"
        variant="secondary"
        icon={<CalendarClock aria-hidden />}
        onClick={() => open({ kind: 'move', item: boardBatch(board, lateBatch) })}
      >
        Move {lateBatch.number}…
      </Button>
    ),
    next.root && (
      <Button
        key="root"
        size="sm"
        variant="secondary"
        icon={<ExternalLink aria-hidden />}
        onClick={() => focusOn((next.root as { batch_id: string }).batch_id)}
      >
        Open {next.root.number}
      </Button>
    ),
    canAllocate && (
      <Button
        key="allocate"
        variant="secondary"
        size="sm"
        onClick={() => open({ kind: 'allocate-line', line: allocating })}
      >
        Allocate a batch…
      </Button>
    ),
  ].filter(Boolean);
  if (buttons.length === 0) return null;
  return (
    <span className="mt-1 flex flex-wrap items-center gap-2" data-testid="line-actions">
      {buttons}
    </span>
  );
}

// ---------------------------------------------------------------- shared

/**
 * One column grid for every day's tables (UI7A-8): the same x for Amount and Check. The
 * last column holds only the batch menu, so Check gets the width (UI7A-101).
 */
const GRID = ['w-24 xl:w-28', 'w-[26%]', 'w-24 xl:w-32', undefined, 'w-14'];
const GRID_TABLE = 'table-fixed min-w-[44rem]';

/**
 * "Plan a batch for this line" (D-074): the day of the promise, what is still to cover, and
 * for a catalogue line its product to search; a custom item's product is the planner's choice.
 */
function planForLine(
  l: AllocatingLine,
  day: string | undefined,
  timeZone: string,
  today: string,
  search?: string,
): NonNullable<Dialogs> {
  return {
    kind: 'plan',
    day: planDayForLine(day, l.promised_at, timeZone, today),
    ...(l.product_id !== null ? { search: search ?? l.label } : {}),
    forLine: {
      order_line_id: l.order_line_id,
      order_number: l.order_number,
      position: l.position,
      label: l.label,
      product_id: l.product_id,
      open: l.open,
      promised_at: l.promised_at,
    },
  };
}

/** The loading board: the banner, then days with their tables (UI7A-11). */
function BoardSkeleton() {
  return (
    <div className="flex flex-col gap-6" aria-busy="true" role="status">
      <span className="sr-only">Loading the plan…</span>
      <Skeleton className="h-24 w-full rounded-lg" />
      {[0, 1].map((i) => (
        <div key={i} className="flex flex-col gap-3">
          <div className="flex items-center gap-3 border-b border-border pb-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-5 w-32" />
          </div>
          <LoadingSkeleton variant="table" count={i === 0 ? 2 : 3} label="Loading the plan" />
        </div>
      ))}
    </div>
  );
}
